import { useState } from 'react';
import type { ProgramCard, OptionCardView, RelativeFacing } from '../api.js';
import { PowerDownChoice } from './PowerDownChoice.js';
import { CARD_LABEL } from '../cardLabels.js';
import { AIM_SIDES, AIM_GLYPH, AIM_LABEL } from '../aimLabels.js';
import { optionTooltip } from '../optionPayload.js';

/**
 * The programming interface: your hand below, five register slots above.
 *
 * Drag and drop, with a click fallback. The fallback is not decoration:
 * HTML5 drag and drop does not work on touch devices at all, and it is
 * awkward for anyone using a keyboard. Click a card, click a slot does the
 * same job and costs about ten lines.
 *
 * Locked registers are shown but not interactive. They already hold the
 * card they will execute again this turn, placed either by last turn's
 * programming or by the Deal's random fill.
 */

/** Turn mode's weapons, of which a robot switches on one a turn. The
 * engine's `TURN_WEAPONS`, by effect. */
const TURN_WEAPONS = new Set([
  'pressorBeam', 'tractorBeam', 'miniHowitzer', 'scrambler', 'radioControl',
  'fireControl',
]);

const FACING_LABEL: Record<string, string> = {
  N: '\u2191 North', E: '\u2192 East', S: '\u2193 South', W: '\u2190 West',
};

/**
 * The four ways an aimed Option can be turned, in the order they are
 * offered: front first, since that is where an untouched card points.
 *
 * Initials rather than arrows. An arrow reads as a compass direction on a
 * screen with a board on it, and these are sides OF THE ROBOT -- F is
 * whichever way the robot happens to be facing. The tooltip spells each
 * one out.
 */
const CARD_GLYPH: Record<string, string> = {
  UTurn: '\u21BA',
  RotateLeft: '\u21B0',
  RotateRight: '\u21B1',
  BackUp: '\u2193',
  Move1: '\u2191',
  Move2: '\u2191\u2191',
  Move3: '\u2191\u2191\u2191',
};

/**
 * Something a robot won at a chop shop, a repair site or in radioactive
 * waste, named so it can be reported where and when it happened.
 *
 * `taken` is an outright grant, `swapped` a chop shop exchange -- the
 * player gave something up for it, so it reads differently -- and
 * `reloaded` a launcher refilled, which is neither but is the third
 * thing a chop shop does and belongs in the same report.
 */
export interface Award {
  name: string;
  kind: 'taken' | 'swapped' | 'reloaded';
  /** What was given up, on a swap that named it. */
  gaveUp?: string;
}

interface ProgrammerProps {
  hand: ProgramCard[];
  locked: boolean[];
  /** Cards already in registers, including locked ones. */
  registers: (ProgramCard | null)[];
  onChange: (registers: (ProgramCard | null)[]) => void;
  onSubmit: () => void;
  /**
   * What this robot won at register 5 and at the end of the turn just
   * played. Registers 1 to 4 report themselves at the pause that follows
   * them, so what reaches here is only what has not been said yet.
   */
  gained: Award[];
  /** Options that can hold a card, and gadgets that can go in a register.
   * Empty in a game without Options, which is when none of this shows. */
  options: OptionCardView[];
  /**
   * Cards set aside on Options, one entry per Option holding one.
   *
   * A list rather than a single entry: a robot with both a Conditional
   * Program and a Flywheel can feed both in one turn if it has the cards
   * to spare, and a single slot made them mutually exclusive.
   */
  setAside: { optionId: string; card: ProgramCard }[];
  onSetAsideChange: (next: { optionId: string; card: ProgramCard }[]) => void;
  /** Gadgets programmed into each register, by Option id. */
  gadgets: string[][];
  onGadgetsChange: (next: string[][]) => void;
  /**
   * Turn-programmed Options switched on for this turn, by Option id.
   *
   * Belongs to the turn rather than to a register, and consumes nothing:
   * the player is only saying "this one applies this turn". Some are
   * worth leaving OFF -- board rotation is often helpful, so a
   * Gyroscopic Stabilizer is not a card you want on every turn.
   */
  activated: string[];
  onActivatedChange: (next: string[]) => void;
  /**
   * Which way each aimed Option is turned for this turn, by Option id.
   *
   * The Shield, the Reflector and the Hydraulic Bumpers are turned rather
   * than switched: they are always working, and the programming decision
   * is which of the robot's four sides they cover. Turned once for the
   * turn, not once per register. An Option with no entry faces front.
   */
  aimed: { optionId: string; facing: RelativeFacing }[];
  onAimedChange: (next: { optionId: string; facing: RelativeFacing }[]) => void;
  /**
   * The Recompile card, when this robot holds one and has not spent it
   * this turn.
   *
   * A card rather than a flag, so the row can carry the wording in its
   * tooltip like every other Option here. Undefined means there is
   * nothing to offer -- no card, or already recompiled.
   */
  recompile?: OptionCardView;
  onRecompile: () => void;
  submitting: boolean;
  submitted: boolean;
  /** Takes the program back so it can be changed and sent again. Offered
   * only once submitted, and the server refuses it once the turn has
   * started -- which is the moment the last player's program lands. */
  onUnsubmit: () => void;
  powerDown: boolean;
  onPowerDownChange: (value: boolean) => void;
  /**
   * Whether this robot is virtual as programming closes.
   *
   * Only a note on the buttons, never a bar on them. Virtual NOW does not
   * mean virtual at the register: a robot returning from the archive
   * stops being virtual partway through the turn, and greying the buttons
   * out cost it every deploy for the whole turn -- including the
   * registers it was solid for. The engine decides at deploy time, one
   * register at a time, and a drop attempted while virtual fizzles with
   * its payload intact (game.ts, deployGadgets).
   */
  virtual: boolean;
  /** Turn 1 only: the player chooses which way their robot faces, and it
   * travels inside this same submission. RULES_SPEC section 2. */
  needsFacing: boolean;
  facing: 'N' | 'E' | 'S' | 'W';
  onFacingChange: (facing: 'N' | 'E' | 'S' | 'W') => void;
}

export function Programmer({
  hand, locked, registers, onChange, onSubmit, submitting, submitted, onUnsubmit, gained,
  powerDown, onPowerDownChange,
  needsFacing, facing, onFacingChange,
  options, setAside, onSetAsideChange, gadgets, onGadgetsChange,
  activated, onActivatedChange, aimed, onAimedChange, virtual,
  recompile, onRecompile,
}: ProgrammerProps) {
  const [held, setHeld] = useState<ProgramCard | null>(null);

  // Options that hold a program card, and gadgets that go into a register.
  // Cards still to be placed in registers. A card can only be set aside
  // if there is one spare after programming — offering the Flywheel with
  // exactly five cards left is offering an impossible choice.
  const toFill = locked.filter((l) => !l).length;
  const spare = hand.length - toFill;

  // "AFTER programming your registers, you may place one of the remaining
  // program cards on this option" -- the Conditional, and the Flywheel
  // says "after programming all five registers". Both mean what they say,
  // and offering the choice earlier was not merely early: a card set
  // aside is off the table, so if the timer then ran out it was neither
  // in a register nor available to fill one, and the player had put it
  // somewhere they could not see it.
  //
  // A register the player empties again takes the row away with it. The
  // card already set aside is left alone rather than snatched back --
  // swapping one card for another passes through "not programmed", and
  // undoing the player's other decision on the way would be its own
  // surprise.
  const programmed = registers.every((card, i) => locked[i] || card !== null);

  const holders = options.filter(
    (o) => (o.effect === 'conditional' || o.effect === 'flywheel')
      && spare > 0 && programmed,
  );
  // Not an empty one: a launcher with nothing left drops nothing, so it is
  // not offered. It is still held, and a chop shop can reload it.
  const launchers = options.filter((o) => o.timing === 'phaseProgrammed'
    && !(o.payload !== undefined && (o.remaining ?? 0) <= 0));

  // Turn-programmed Options that are switched on rather than fed a card.
  // The Flywheel is turn-programmed too but works by holding a card, so
  // it is already offered under "Set a card aside" and must not appear
  // here as well.
  const switches = options.filter(
    (o) => o.timing === 'turnProgrammed' && o.effect !== 'flywheel'
      && !o.directional,
  );

  // Turn-programmed Options that are TURNED rather than switched: the
  // card works either way and what is programmed is the side it covers.
  const dials = options.filter((o) => o.directional);

  // A turn-mode weapon is one of a set: switching one on switches off any
  // other, since the engine takes one weapon a turn.
  const isTurnWeapon = (optionId: string) => {
    const option = options.find((o) => o.id === optionId);
    return option?.turnMode === true && TURN_WEAPONS.has(option.effect);
  };

  const toggleActivated = (optionId: string) => {
    onActivatedChange(
      activated.includes(optionId)
        ? activated.filter((id) => id !== optionId)
        : [
          ...(isTurnWeapon(optionId) ? activated.filter((id) => !isTurnWeapon(id)) : activated),
          optionId,
        ],
    );
  };

  // Front is the default rather than a stored value, so an Option turned
  // back to the front drops out of the list entirely and the submission
  // stays as small as what the player actually changed.
  const facingOf = (optionId: string): RelativeFacing =>
    aimed.find((a) => a.optionId === optionId)?.facing ?? 'front';

  const aimAt = (optionId: string, side: RelativeFacing) => {
    const rest = aimed.filter((a) => a.optionId !== optionId);
    onAimedChange(side === 'front' ? rest : [...rest, { optionId, facing: side }]);
  };

  // A card in the hand is available unless it is already in a register, or
  // set aside on an Option.
  const placed = new Set(
    registers.filter((c): c is ProgramCard => c !== null).map((c) => c.priority),
  );
  for (const entry of setAside) placed.add(entry.card.priority);
  // Lowest priority first: the U-turns and Back Ups on the left, the Move
  // 3s on the right. A dealt hand arrives in draw order, which is no
  // order at all to choose from -- and it stays that way on the robot,
  // since a sort here is a display choice and the hand as recorded should
  // say what was drawn.
  //
  // Safe to sort: nothing reads the hand in order. Every rule that takes
  // cards out of it -- the auto-fill, a power-down's random program --
  // shuffles first, and everything else looks a card up by priority.
  const available = hand
    .filter((c) => !placed.has(c.priority))
    .sort((a, b) => a.priority - b.priority);

  const toggleGadget = (slot: number, optionId: string) => {
    const next = gadgets.map((g) => g.slice());
    while (next.length < 5) next.push([]);

    const already = next[slot].includes(optionId);
    if (already) {
      next[slot] = next[slot].filter((id) => id !== optionId);
      onGadgetsChange(next);
      return;
    }

    // An option with ammo can be programmed into more than one register
    // in a turn, limited by the ammo it has left. Anything else is a
    // one-shot and moves rather than multiplies, so selecting a new
    // register clears the old one.
    const option = options.find((o) => o.id === optionId);
    const ammo = option?.payloadKind === 'ammo' ? (option.remaining ?? 0) : 1;
    if (ammo <= 1) {
      for (let i = 0; i < next.length; i++) {
        next[i] = next[i].filter((id) => id !== optionId);
      }
    } else {
      // Already at the ammo limit: the click does nothing rather than
      // building a program the engine will reject.
      const used = next.reduce((n, ids) => n + (ids.includes(optionId) ? 1 : 0), 0);
      if (used >= ammo) return;
    }

    // ONE gadget per register: a robot deploys one thing at a time, so
    // choosing a second replaces the first rather than joining it.
    next[slot] = [optionId];

    onGadgetsChange(next);
  };

  const place = (card: ProgramCard, slot: number) => {
    if (locked[slot]) return;
    const next = registers.slice();
    // Placing onto an occupied slot returns the previous card to the hand
    // simply by dropping the reference; `available` recomputes from this.
    next[slot] = card;
    // The same card cannot occupy two slots.
    for (let i = 0; i < next.length; i++) {
      if (i !== slot && next[i]?.priority === card.priority) next[i] = null;
    }
    onChange(next);
    setHeld(null);
  };

  const clear = (slot: number) => {
    if (locked[slot]) return;
    const next = registers.slice();
    next[slot] = null;
    onChange(next);
  };

  // Only the registers the player can actually fill. A locked register is
  // not theirs to program: it already holds its card, the submission
  // deliberately sends null for it, and the engine rejects any attempt to
  // set one. Requiring a card there meant that if the locked slot arrived
  // empty for any reason the button greyed out with nothing the player
  // could do about it, and the game could not proceed at all. The button
  // should never depend on data the player cannot supply.
  const complete = registers.every((c, i) => c !== null || locked[i]);

  return (
    <section className="programmer">
      {needsFacing && (
        <div className="facing-pick">
          <p className="eyebrow">Which way does your robot face to start?</p>
          <div className="row">
            {(['N', 'E', 'S', 'W'] as const).map((d) => (
              <button
                key={d}
                className={facing === d ? '' : 'secondary'}
                onClick={() => onFacingChange(d)}
                disabled={submitted}
              >
                {FACING_LABEL[d]}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="registers" role="list">
        {registers.map((card, i) => (
          <div
            key={i}
            role="listitem"
            // A submitted register is as fixed as a locked one. Every
            // other control on this screen takes `disabled={submitted}`;
            // a `div` cannot be disabled, so the same guard is written
            // into the handlers by hand -- and without it the cards could
            // be rearranged after Submit, on screen only, while the turn
            // ran the program that was actually sent.
            className={`slot${locked[i] || submitted ? ' locked' : ''}`
              + `${held && !locked[i] && !submitted ? ' target' : ''}`}
            onDragOver={(e) => { if (!locked[i] && !submitted) e.preventDefault(); }}
            onDrop={(e) => {
              e.preventDefault();
              if (submitted) return;
              const raw = e.dataTransfer.getData('application/json');
              if (raw) place(JSON.parse(raw) as ProgramCard, i);
            }}
            onClick={() => {
              if (submitted) return;
              if (held) place(held, i);
              else if (card && !locked[i]) clear(i);
            }}
          >
            <span className="slot-number">{i + 1}</span>

            {/* A locked register carries a card the player did not put
                there and cannot move, and the only sign of it was the
                slot's own shading. Said here, above the card, the way a
                gadget is said below it. */}
            {locked[i] && <span className="slot-lock">Locked</span>}

            {card ? (
              <span className="card-face">
                <span className="card-priority num">{card.priority}</span>
                <span className="card-glyph">{CARD_GLYPH[card.type]}</span>
                <span className="card-name">{CARD_LABEL[card.type]}</span>
              </span>
            ) : (
              <span className="slot-empty">empty</span>
            )}

            {/* Gadgets fire BEFORE this register's card, so a robot can
                drop something and move off it. */}
            {(gadgets[i] ?? []).length > 0 && (
              <span className="slot-gadgets">
                {(gadgets[i] ?? []).map((id) => (
                  <span key={id} className="slot-gadget">
                    {options.find((o) => o.id === id)?.name ?? id}
                  </span>
                ))}
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="hand" role="list" style={{ marginTop: '1rem' }}>
        {available.map((card) => (
          <button
            key={card.priority}
            role="listitem"
            className={`card${held?.priority === card.priority ? ' held' : ''}`}
            // The one control on this screen that was missing the guard
            // its siblings all carry. `draggable` is turned off with it:
            // a disabled button still drags, so the card could be dropped
            // into a register the click could no longer reach.
            disabled={submitted}
            draggable={!submitted}
            onDragStart={(e) => {
              e.dataTransfer.setData('application/json', JSON.stringify(card));
              e.dataTransfer.effectAllowed = 'move';
              setHeld(card);
            }}
            onDragEnd={() => setHeld(null)}
            onClick={() => setHeld(held?.priority === card.priority ? null : card)}
            // Straight into the first register with room, so a program
            // built in order is five double clicks rather than ten single
            // ones. The single-click pick-up is untouched and is still
            // what you use to choose the slot.
            //
            // The two clicks that make up the double have already toggled
            // `held` on and off again by the time this runs, which leaves
            // it where `place` wants it anyway.
            onDoubleClick={() => {
              const slot = registers.findIndex((c, i) => c === null && !locked[i]);
              if (slot >= 0) place(card, slot);
            }}
          >
            <span className="card-priority num">{card.priority}</span>
            <span className="card-glyph">{CARD_GLYPH[card.type]}</span>
            <span className="card-name">{CARD_LABEL[card.type]}</span>
          </button>
        ))}
        {available.length === 0 && (
          // A robot on 9 damage is dealt nothing at all and programs
          // nothing: its five locked registers above are the whole turn.
          // "Every card is placed" is true but reads as an accident.
          <p className="muted">
            {hand.length === 0 ? 'No cards this turn.' : 'Every card is placed.'}
          </p>
        )}
      </div>

      {/* Directly under the hand, which is what it replaces and what the
          player is looking at when they decide the hand is not worth
          programming. Gone once it has been spent -- once per turn -- so
          the row is its own answer to whether it is still available, and
          off after a submission: "before your robot is programmed".

          Hidden with an empty hand too. On 9 damage there is no hand to
          replace, so the card would draw nothing and spend the point that
          destroys the robot. */}
      {recompile && hand.length > 0 && (
        <div className="gadget-grid">
          <div className="gadget-row">
            <span className="gadget-name" title={recompile.text}>
              {recompile.name}
            </span>
            <button
              className="secondary tiny"
              disabled={submitting || submitted}
              title={recompile.text}
              onClick={onRecompile}
            >
              New hand, 1 damage
            </button>
          </div>
        </div>
      )}

      {launchers.length > 0 && (
        <div className="stack" style={{ gap: '0.3rem' }}>
          <div className="gadget-grid">
            {launchers.map((launcher) => (
              <div key={launcher.id} className="gadget-row">
                <span className="gadget-name" title={optionTooltip(launcher)}>
                  {launcher.name}
                  {launcher.remaining !== undefined && (
                    <span className="option-payload num"> {launcher.remaining}</span>
                  )}
                </span>
                <span className="row" style={{ gap: '0.2rem' }}>
                  {[0, 1, 2, 3, 4].map((slot) => (
                    <button
                      key={slot}
                      className={
                        (gadgets[slot] ?? []).includes(launcher.id)
                          ? 'gadget-slot on'
                          : 'gadget-slot'
                      }
                      disabled={locked[slot] || submitted}
                      title={virtual
                        ? 'Your robot is virtual: a register it is still virtual for '
                          + 'drops nothing and keeps the payload'
                        : undefined}
                      onClick={() => toggleGadget(slot, launcher.id)}
                    >
                      {slot + 1}
                    </button>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {switches.length > 0 && (
        <div className="stack" style={{ gap: '0.3rem' }}>
          <div className="gadget-grid">
            {switches.map((option) => (
              <div key={option.id} className="gadget-row">
                <span className="gadget-name" title={option.text}>
                  {option.name}
                </span>
                <button
                  className={activated.includes(option.id) ? 'gadget-slot on' : 'gadget-slot'}
                  style={{ width: 'auto', padding: '0 0.6rem' }}
                  disabled={submitted}
                  onClick={() => toggleActivated(option.id)}
                >
                  {activated.includes(option.id) ? 'On' : 'Off'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {dials.length > 0 && (
        <div className="stack" style={{ gap: '0.3rem' }}>
          <div className="gadget-grid">
            {dials.map((option) => (
              <div key={option.id} className="gadget-row">
                <span className="gadget-name" title={option.text}>
                  {option.name}
                </span>
                <span className="row" style={{ gap: '0.2rem' }}>
                  {AIM_SIDES.map((side) => (
                    <button
                      key={side}
                      className={
                        facingOf(option.id) === side ? 'gadget-slot on' : 'gadget-slot'
                      }
                      disabled={submitted}
                      title={AIM_LABEL[side]}
                      onClick={() => aimAt(option.id, side)}
                    >
                      {AIM_GLYPH[side]}
                    </button>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {holders.length > 0 && (
        <div className="stack" style={{ gap: '0.3rem' }}>
          {holders.map((holder) => {
            const mine = setAside.find((e) => e.optionId === holder.id)?.card ?? null;
            const heldAlready = holder.heldCard;
            return (
              <div key={holder.id} className="gadget-row">
                <span className="gadget-name" title={holder.text}>{holder.name}</span>
                <span className="holder-action">
                  {heldAlready ? (
                    <span className="mono muted" style={{ fontSize: '0.75rem' }}>
                      already holding a card
                    </span>
                  ) : mine ? (
                    // The card alone. What the button DOES belongs in the
                    // tooltip: the row is read at a glance for what is on
                    // the Option, and the words outnumbered the card.
                    <button
                      className="secondary tiny"
                      onClick={() => onSetAsideChange(
                        setAside.filter((e) => e.optionId !== holder.id),
                      )}
                      disabled={submitted}
                      title="Take this card back"
                    >
                      {CARD_LABEL[mine.type]} {mine.priority}
                    </button>
                  ) : held ? (
                    // "one of the remaining MOVEMENT cards" for a Flywheel;
                    // the Conditional takes anything. Saying so beats a
                    // greyed button with no explanation.
                    holder.effect === 'flywheel'
                      && !['Move1', 'Move2', 'Move3', 'BackUp'].includes(held.type) ? (
                        <span className="mono muted" style={{ fontSize: '0.75rem' }}>
                          the flywheel takes a movement card, not {CARD_LABEL[held.type]}
                        </span>
                      ) : (
                        <button
                          className="tiny"
                          onClick={() => {
                            onSetAsideChange([
                              ...setAside.filter((e) => e.optionId !== holder.id),
                              { optionId: holder.id, card: held },
                            ]);
                            setHeld(null);
                          }}
                          disabled={submitted}
                        >
                          Put {CARD_LABEL[held.type]} here
                        </button>
                      )
                  ) : (
                    <span className="mono muted" style={{ fontSize: '0.75rem' }}>
                      pick a{holder.effect === 'flywheel' ? ' movement' : ''} card, then click here
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Power down is decided here rather than on a screen of its own.
          The engine still runs it as a separate step after programming;
          this is only where the answer is collected. The same control
          appears on that step for a turn whose program was forced
          through, so it lives in one place. */}
      <PowerDownChoice
        value={powerDown}
        onChange={onPowerDownChange}
        disabled={submitted}
      />

      {/* Options gained since the last time this player programmed. The
          log records them, but the log scrolls and a new Option changes
          how the turn should be played, so it is worth saying here where
          the player is already looking. */}
      {gained.length > 0 && (
        <p className="notice ok upgrade">
          {gained.map((g, i) => (
            <span key={`${g.kind}-${g.name}`}>
              {i > 0 && <br />}
              {g.kind === 'swapped' ? 'You have just swapped to: '
                : g.kind === 'reloaded' ? 'You have just reloaded: '
                  : 'You have just upgraded to: '}
              <strong>{g.name}</strong>
            </span>
          ))}
        </p>
      )}

      <button onClick={onSubmit} disabled={!complete || submitting || submitted}>
        {submitted ? 'Programmed' : 'Submit Program'}
      </button>
      {submitted && (
        <button
          onClick={onUnsubmit}
          disabled={submitting}
          title={'Take it back and change it. The cards stay where they are,'
            + ' and are still what plays if your time runs out.'}
        >
          Unsubmit
        </button>
      )}
    </section>
  );
}
